import { Component, useCallback, useEffect, useMemo, useState, type ErrorInfo, type ReactNode } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  makeStyles,
  tokens,
  Text,
  Badge,
  Button,
  Input,
  Spinner,
  Tooltip,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Tab,
  TabList,
  Dropdown,
  Option,
  MenuItem,
  type SelectTabData,
  type SelectTabEvent,
} from '@fluentui/react-components';
import {
  Flow16Regular,
  Apps16Regular,
  Folder16Regular,
  AddRegular,
  ArrowClockwise16Regular,
  ArrowSortDownRegular,
  ArrowSortUpRegular,
  SearchRegular,
  Open16Regular,
} from '@fluentui/react-icons';
import { useAuth } from '@/auth';
import { useAppStore } from '@/stores/appStore';
import { useRecentWorkStore, type RecentEntry } from '@/stores/recentWorkStore';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useProjectsMultiRegion, useProviderLocations, useAutomationProjectArmRefs, armRefKey, azureResourceKeys } from '@/hooks/useAzureResources';
import { syncOnboardingFromData, shouldShowOnboarding } from '@/hooks/useOnboardingProgress';
import { OnboardingChecklist } from '@/components/Onboarding';
import { RegionFilter } from '@/components/ProjectList/RegionFilter';
import { mapProvisioningState, type StatusType } from '@/components/shared/StatusBadge';
import { TableSkeleton } from '@/components/shared/TableSkeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import {
  EntityNameCell,
  DotStatusBadge,
  TablePaginationFooter,
  DataTablePanel,
  usePagination,
  RowActionsMenu,
  type DotStatusKind,
} from '@/components/shared/DataTable';
import { CreateProjectDialog } from '@/components/Project/CreateProjectDialog';
import { paths } from '@/routes';
import { azurePortalResourceUrl } from '@/constants/links';
import { CREATE_FLAG_PARAM } from '@/routes/queryParams';
import { formatDateTime, getRelativeTime } from '@/utils/dateTime';
import { normalizeLocation } from '@/utils/system';

// ── Helpers ────────────────────────────────────────────────────────────

function getGreeting(name?: string | null): string {
  const hour = new Date().getHours();
  const displayName = name ?? 'there';
  if (hour < 12) return `Good morning, ${displayName}`;
  if (hour < 18) return `Good afternoon, ${displayName}`;
  return `Good evening, ${displayName}`;
}

function formatRelativeTime(timestamp: number): string {
  return getRelativeTime(timestamp, { style: 'short' });
}

// Lookup map for recent-entry type metadata so icon + label stay in
// lock-step across the dashboard.
const RECENT_ENTRY_TYPE_META: Record<
  RecentEntry['type'],
  { Icon: typeof Flow16Regular; label: string }
> = {
  workflow: { Icon: Flow16Regular, label: 'Workflow' },
  project: { Icon: Folder16Regular, label: 'Project' },
  app: { Icon: Apps16Regular, label: 'Application' },
};


// ── Section Error Boundary ─────────────────────────────────────────────

interface SectionErrorBoundaryProps {
  children: ReactNode;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
}

class SectionErrorBoundary extends Component<SectionErrorBoundaryProps, SectionErrorBoundaryState> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): SectionErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[DashboardHomePage] Section error:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

// ── Constants & Styles ─────────────────────────────────────────────────

const RECENT_WORK_LIMIT = 10;
// Visual offset so the EmptyState sits below the toolbar rather than flush against it.
const EMPTY_STATE_TOP_OFFSET = '60px';

const useStyles = makeStyles({
  page: {
    // Bounded vertical layout so the inner `DataTablePanel` instances can
    // pin their pagination footers to the viewport bottom. The page used
    // to scroll as a whole (`overflow: auto`); now the hero/onboarding
    // sit fixed at the top and the active tab's table panel scrolls
    // independently — matching the workflow & app list pages.
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXXL,
    padding: tokens.spacingVerticalXXXL,
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  heroSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXXL,
  },
  greetingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  greeting: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  // Date / metadata text — matches the Apps table "Date modified" cell.
  recentMetadata: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  // Explicit column widths so the Recent Activity table distributes columns
  // predictably and visually matches the Apps table layout.
  recentColName: { width: '50%' },
  recentColType: { width: '25%' },
  recentColDate: { width: '25%' },
  actionsCell: {
    width: '48px',
    whiteSpace: 'nowrap',
    textAlign: 'right',
    paddingRight: tokens.spacingHorizontalM,
  },
  viewAllLink: {
    alignSelf: 'flex-start',
    color: tokens.colorBrandForeground1,
    textDecorationLine: 'none',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
  tabBar: {
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  tabContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
    paddingBlockStart: tokens.spacingVerticalS,
    // Claim the remaining vertical space inside `.page` so the
    // `DataTablePanel` underneath the toolbar can pin its footer.
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  // Onboarding renders instead of the tab panels when there are no projects.
  // It doesn't host a `DataTablePanel`, so it doesn't need bounded layout —
  // but `.page` is `overflow: hidden`, so any content taller than the viewport
  // (the OnboardingChecklist can be tall) would clip. Allow this branch to
  // scroll within the page's available height.
  onboardingScroll: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXXL,
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  progressHint: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXL,
    width: '100%',
  },
  searchInput: {
    width: '320px',
  },
  region: {
    marginLeft: tokens.spacingHorizontalS,
  },
  ownerFilter: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  ownerLabel: {
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
  },
  ownerDropdown: {
    minWidth: '160px',
  },
  emptyStateOffset: {
    marginTop: `calc(${EMPTY_STATE_TOP_OFFSET} - ${tokens.spacingVerticalM})`,
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  paginationInfo: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none' as const,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    '&:hover': {
      color: tokens.colorBrandForeground1,
    },
  },
});

// ── DashboardToolbar (shared between Recents and Projects tabs) ────────

interface DashboardToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  searchAriaLabel: string;
  regionValue: string | null;
  onRegionChange: (region: string | null) => void;
  statusValue: StatusType | 'all';
  onStatusChange: (status: StatusType | 'all') => void;
  isFetchingMore: boolean;
  loadedRegions: number;
  totalRegions: number;
  onRefresh: () => void;
  isRefreshDisabled: boolean;
  refreshTooltip: string;
  refreshAriaLabel: string;
  onCreateProject: () => void;
  /** Optional content rendered in the right-aligned group, before Refresh. */
  rightExtras?: ReactNode;
}

const DashboardToolbar = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchAriaLabel,
  regionValue,
  onRegionChange,
  statusValue,
  onStatusChange,
  isFetchingMore,
  loadedRegions,
  totalRegions,
  onRefresh,
  isRefreshDisabled,
  refreshTooltip,
  refreshAriaLabel,
  onCreateProject,
  rightExtras,
}: DashboardToolbarProps) => {
  const styles = useStyles();
  return (
    <div className={styles.toolbar}>
      <Input
        size="small"
        contentBefore={<SearchRegular />}
        placeholder={searchPlaceholder}
        value={searchValue}
        onChange={(_e, data) => onSearchChange(data.value)}
        aria-label={searchAriaLabel}
        className={styles.searchInput}
      />
      <RegionFilter value={regionValue} onChange={onRegionChange} />
      <div className={styles.ownerFilter}>
        <Text size={200} weight="semibold" className={styles.ownerLabel}>Status:</Text>
        <Dropdown
          size="small"
          value={statusValue === 'all' ? 'All' : statusValue}
          selectedOptions={[statusValue]}
          onOptionSelect={(_e, data) => onStatusChange((data.optionValue as StatusType | 'all') ?? 'all')}
          className={styles.ownerDropdown}
          aria-label="Status filter"
        >
          <Option value="all">All</Option>
          <Option value="Ready">Ready</Option>
          <Option value="Provisioning">Provisioning</Option>
          <Option value="Failed">Failed</Option>
        </Dropdown>
      </div>
      {isFetchingMore && (
        <Text className={styles.progressHint}>
          <Spinner size="tiny" />
          Loading regions ({loadedRegions}/{totalRegions})...
        </Text>
      )}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
        {rightExtras}
        <Tooltip content={refreshTooltip} relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={isFetchingMore ? <Spinner size="tiny" /> : <ArrowClockwise16Regular />}
            onClick={onRefresh}
            disabled={isRefreshDisabled}
            aria-label={refreshAriaLabel}
          >
            Refresh
          </Button>
        </Tooltip>
        <Button
          appearance="primary"
          size="small"
          icon={<AddRegular />}
          onClick={onCreateProject}
        >
          Create project
        </Button>
      </div>
    </div>
  );
};

// ── Page Component ─────────────────────────────────────────────────────

export const DashboardHomePage = () => {
  const styles = useStyles();
  const { account } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenantId: urlTenantId, locationName: urlLocationName } = useParams<{ tenantId?: string; locationName?: string }>();
  const locationInStore = useAppStore(s => s.selectedLocation);
  const selectedTenantId = useAppStore(s => s.selectedTenantId);
  const setSelectedLocation = useAppStore(s => s.setSelectedLocation);
  const recentEntries = useRecentWorkStore((s) => s.entries);
  const clearAllRecent = useRecentWorkStore((s) => s.clearAll);
  const queryClient = useQueryClient();

  // Routes like `/` and `/location/:locationName` don't carry tenantId in the URL,
  // so fall back to the store value. Entries are recorded with the store's tenantId
  // (see AppLayout/ProjectLayout/WorkflowDesignerPage), so this keeps filtering consistent.
  const tenantId = urlTenantId ?? selectedTenantId ?? undefined;

  // Read tab and region from URL query params (e.g., /?tab=projects&region=brazilsouth)
  const urlTab = useMemo(() => searchParams.get('tab'), [searchParams]);
  const urlRegion = useMemo(() => normalizeLocation(searchParams.get('region')), [searchParams]);

  // Sync region from either URL path (/location/{region}) or query param (?region=...) when URL changes externally.
  // When the URL has no region but the store does, clear the store so the dashboard
  // matches the URL (covers cases where setRegionFilter's setState didn't commit
  // before the route remounted, and stale persisted selectedLocation values).
  useEffect(() => {
    const effectiveRegion = urlLocationName ?? urlRegion ?? '';
    if (effectiveRegion && effectiveRegion !== locationInStore) {
      setSelectedLocation(effectiveRegion);
    } else if (!effectiveRegion && locationInStore) {
      setSelectedLocation(null);
    }
  }, [setSelectedLocation, urlLocationName, urlRegion, locationInStore]);

  const [projectSearch, setProjectSearch] = useState('');
  const [recentSearch, setRecentSearch] = useState('');
  const [projectStatusFilter, setProjectStatusFilter] = useState<StatusType | 'all'>('all');
  const [recentStatusFilter, setRecentStatusFilter] = useState<StatusType | 'all'>('all');

  // Local-only favorites for projects (not yet persisted to the backend).
  const [favoriteProjects, setFavoriteProjects] = useState<Set<string>>(() => new Set());
  const toggleProjectFavorite = useCallback((projectId: string, next: boolean) => {
    setFavoriteProjects((prev) => {
      const updated = new Set(prev);
      if (next) updated.add(projectId); else updated.delete(projectId);
      return updated;
    });
  }, []);

  const projectStatusToDot = useCallback((status: StatusType): DotStatusKind => {
    switch (status) {
      case 'Ready':
      case 'Active':
        return 'Active';
      case 'Failed':
        return 'Error';
      case 'Provisioning':
        return 'Provisioning';
      case 'Inactive':
      default:
        return 'Inactive';
    }
  }, []);

  // Multi-region: when a region is selected, only fetch that one region
  // to avoid unnecessary network requests for all other regions.
  const { projects, isLoading: projectsLoading, isFetchingMore, loadedRegions, totalRegions } = useProjectsMultiRegion(locationInStore);
  const { data: locations } = useProviderLocations();
  const { data: projectArmRefs } = useAutomationProjectArmRefs();

  const regionDisplayName = useMemo(() => {
    const map = new Map<string, string>();
    for (const loc of locations ?? []) {
      map.set(loc.name, loc.displayName);
    }
    return (name: string) => map.get(name) ?? name;
  }, [locations]);

  const hasProjects = useMemo(() => projects.length > 0, [projects]);
  

  const navigate = useNavigate();

  const setRegionFilter = useCallback((region: string | null) => {
    setSelectedLocation(region ?? null);

    // When mounted under `/location/:locationName` (or its tenant-prefixed
    // variant), the route param drives the hydration effect above — so
    // changing or clearing the region requires rewriting the path itself.
    // Mutating only the `region` query string leaves the path intact, and
    // the hydration effect immediately snaps the store back to the path's
    // region. Update the path to keep URL and store in sync.
    if (urlLocationName) {
      const tenantBase = urlTenantId ? `/tenant/${encodeURIComponent(urlTenantId)}` : '';
      const newPath = region
        ? `${tenantBase}/location/${encodeURIComponent(region)}`
        : (tenantBase || '/');
      const next = new URLSearchParams(searchParams);
      next.delete('region');
      const qs = next.toString();
      navigate(qs ? `${newPath}?${qs}` : newPath, { replace: true });
      return;
    }

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      // Preserve whatever tab the user is on; don't force them to Projects.
      if (region) {
        next.set('region', region);
      } else {
        next.delete('region');
      }
      return next;
    }, { replace: true });
  }, [navigate, searchParams, setSearchParams, setSelectedLocation, urlLocationName, urlTenantId]);

  const handleRefreshProjects = useCallback(() => {
    // Reset all project queries (prefix match catches all tenants/regions)
    queryClient.resetQueries({ queryKey: azureResourceKeys.projectsPrefix() });
    queryClient.resetQueries({ queryKey: azureResourceKeys.projectArmRefsPrefix() });
  }, [queryClient]);

  // Auto-sync onboarding from actual data
  useEffect(() => {
    if (!projectsLoading) {
      syncOnboardingFromData({ hasProjects });
    }
  }, [hasProjects, projectsLoading]);

  // Only show onboarding when viewing ALL regions with no projects.
  // Only show onboarding when ALL regions have been queried and none have projects.
  // If a region filter is active, empty results just mean that region has no projects.
  const isRegionFiltered = !!locationInStore;
  const showOnboarding = useMemo(
    () => !projectsLoading && !isFetchingMore && !hasProjects && !isRegionFiltered && shouldShowOnboarding(),
    [projectsLoading, isFetchingMore, hasProjects, isRegionFiltered],
  );

  const greeting = useMemo(() => getGreeting(account?.name), [account?.name]);

  // Recent work entries for current tenant
  const [recentSortColumn, setRecentSortColumn] = useState<'name' | 'type' | 'lastVisitedAt'>('lastVisitedAt');
  const [recentSortDirection, setRecentSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleRecentSort = useCallback((column: 'name' | 'type' | 'lastVisitedAt') => {
    if (recentSortColumn === column) {
      setRecentSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setRecentSortColumn(column);
      // Default to descending for the time column (most-recent first),
      // ascending for name/type (alphabetical first).
      setRecentSortDirection(column === 'lastVisitedAt' ? 'desc' : 'asc');
    }
  }, [recentSortColumn]);

  // Tenant-scoped, sorted recent entries (pre-filter). Drives whether the
  // Recents tab is shown at all, so filtering an empty result doesn't make
  // the tab disappear mid-interaction.
  const scopedRecent = useMemo(() => {
    if (!tenantId) return [];
    const scoped = recentEntries.filter((e) => e.tenantId === tenantId);
    const dir = recentSortDirection === 'asc' ? 1 : -1;
    const sorted = [...scoped].sort((a, b) => {
      switch (recentSortColumn) {
        case 'name':
          return a.displayName.localeCompare(b.displayName) * dir;
        case 'type': {
          const ta = RECENT_ENTRY_TYPE_META[a.type].label;
          const tb = RECENT_ENTRY_TYPE_META[b.type].label;
          return ta.localeCompare(tb) * dir;
        }
        case 'lastVisitedAt':
        default:
          return (a.lastVisitedAt - b.lastVisitedAt) * dir;
      }
    });
    return sorted.slice(0, RECENT_WORK_LIMIT);
  }, [recentEntries, tenantId, recentSortColumn, recentSortDirection]);

  // Map of project name → provisioning status, used by the Recents Status
  // filter. Recent entries store the parent project name (not ARM id), so
  // we join on `name`.
  const projectStatusByName = useMemo(() => {
    const map = new Map<string, StatusType>();
    for (const p of projects) {
      map.set(p.name, mapProvisioningState(p.properties?.provisioningState ?? 'Unknown'));
    }
    return map;
  }, [projects]);

  const displayedRecent = useMemo(() => {
    let list = scopedRecent;
    const query = recentSearch.trim().toLowerCase();
    if (query) {
      list = list.filter((e) => e.displayName.toLowerCase().includes(query));
    }
    if (locationInStore) {
      list = list.filter((e) => e.location === locationInStore);
    }
    if (recentStatusFilter !== 'all') {
      list = list.filter((e) => projectStatusByName.get(e.project) === recentStatusFilter);
    }
    return list;
  }, [scopedRecent, recentSearch, locationInStore, recentStatusFilter, projectStatusByName]);

  const hasRecentActivity = scopedRecent.length > 0;

  // Tab selection precedence: URL `?tab=` > auto-derived from whether
  // recent activity exists. User clicks update `?tab=` (see onTabSelect),
  // so the URL is the single source of truth for explicit picks and the
  // auto-derived path keeps the tab reactive when entries arrive after
  // the initial render.
  // Guard: never settle on 'recent' when there are no entries to show.
  const candidateTab =
    urlTab === 'recent' || urlTab === 'projects'
      ? urlTab
      : hasRecentActivity ? 'recent' : 'projects';
  const selectedTab = candidateTab === 'recent' && !hasRecentActivity ? 'projects' : candidateTab;

  // Browser tab title: reflect the currently-active dashboard tab so users
  // with multiple tabs open can tell Projects from Recents at a glance.
  useDocumentTitle([selectedTab === 'recent' ? 'Recents' : 'Projects']);

  const onTabSelect = useCallback((_: SelectTabEvent, data: SelectTabData) => {
    const value = data.value as string;
    // Write the click to `?tab=` so URL stays the source of truth for
    // explicit picks (including when the page was deep-linked with ?tab=).
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Filter projects by local region filter, search, status, owner; then sort and paginate
  type ProjectSortColumn = 'name' | 'region' | 'modified';
  const [projectSortColumn, setProjectSortColumn] = useState<ProjectSortColumn>('name');
  const [projectSortDirection, setProjectSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleProjectSort = useCallback((column: ProjectSortColumn) => {
    if (projectSortColumn === column) {
      setProjectSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setProjectSortColumn(column);
      setProjectSortDirection('asc');
    }
  }, [projectSortColumn]);

  const filteredProjects = useMemo(() => {
    let list = projects;
    if (projectSearch.trim()) {
      const query = projectSearch.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(query));
    }
    if (projectStatusFilter !== 'all') {
      list = list.filter((p) => mapProvisioningState(p.properties?.provisioningState ?? 'Unknown') === projectStatusFilter);
    }
    const dir = projectSortDirection === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      switch (projectSortColumn) {
        case 'name':
          return dir * a.name.localeCompare(b.name);
        case 'region':
          return dir * (a.location ?? '').localeCompare(b.location ?? '');
        case 'modified':
          return dir * ((a.systemData?.lastModifiedAt ?? '').localeCompare(b.systemData?.lastModifiedAt ?? ''));
        default:
          return 0;
      }
    });
  }, [projects, projectSearch, projectStatusFilter, projectSortColumn, projectSortDirection]);

  // Projects table pagination (page size persisted across reloads).
  const projectsPagination = usePagination<typeof filteredProjects[number]>({
    totalItems: filteredProjects.length,
    storageKey: 'otto.dashboardProjects.pageSize',
    resetDeps: [projectSearch, projectStatusFilter, locationInStore, projectSortColumn, projectSortDirection],
  });
  const paginatedProjects = useMemo(
    () => projectsPagination.paginate(filteredProjects),
    [projectsPagination, filteredProjects],
  );

  // Recent activity pagination — same hook, separate storage key.
  const recentPagination = usePagination<typeof displayedRecent[number]>({
    totalItems: displayedRecent.length,
    storageKey: 'otto.dashboardRecent.pageSize',
    resetDeps: [recentSearch, recentStatusFilter, locationInStore, recentSortColumn, recentSortDirection],
  });
  const paginatedRecent = useMemo(
    () => recentPagination.paginate(displayedRecent),
    [recentPagination, displayedRecent],
  );

  const [showCreateProjectDialog, setShowCreateProjectDialog] = useState(false);

  // Header pill "+ Create new project" navigates here with ?create=1. Open
  // the existing confirm dialog on mount and strip the flag so a reload
  // doesn't re-trigger it.
  useEffect(() => {
    if (searchParams.get(CREATE_FLAG_PARAM) === '1') {
      setShowCreateProjectDialog(true);
      const next = new URLSearchParams(searchParams);
      next.delete(CREATE_FLAG_PARAM);
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleCreateProject = useCallback(() => {
    setShowCreateProjectDialog(true);
  }, []);

  const handleProjectCreated = useCallback(() => {
    // Refresh the projects list after a new project is created
    handleRefreshProjects();
  }, [handleRefreshProjects]);

  return (
    <div className={styles.page}>
      {/* Greeting + Feature Cards */}
      <SectionErrorBoundary>
        <div className={styles.heroSection}>
          <div className={styles.greetingRow}>
            <Text className={styles.greeting}>{greeting}</Text>
          </div>
        </div>
      </SectionErrorBoundary>

      {/* Onboarding (when no projects) */}
      {showOnboarding && (
        <SectionErrorBoundary>
          <div className={styles.onboardingScroll}>
            <div className={styles.region}>
              <RegionFilter value={locationInStore} onChange={setRegionFilter} />
            </div>

            <OnboardingChecklist />
          </div>
        </SectionErrorBoundary>
      )}

      {/* Tabbed content: Recent Activity | Projects */}
      {!showOnboarding && (
        <SectionErrorBoundary>
          <TabList
            selectedValue={selectedTab}
            onTabSelect={onTabSelect}
            className={styles.tabBar}
            aria-label="Dashboard sections"
          >
            {hasRecentActivity && <Tab value="recent">Recents</Tab>}
            <Tab value="projects">
              Projects
              {hasProjects && (
                <>
                  {' '}<Badge appearance="filled" color="informative" size="small">{filteredProjects.length}</Badge>
                </>
              )}
            </Tab>
          </TabList>

          <div className={styles.tabContent}>
            {selectedTab === 'recent' && (
              scopedRecent.length === 0 ? (
                <EmptyState
                  title="No recent activity"
                  description="Start by browsing your projects or creating a new workflow."
                />
              ) : (
                <>
                  <DashboardToolbar
                    searchValue={recentSearch}
                    onSearchChange={setRecentSearch}
                    searchPlaceholder="Search recents..."
                    searchAriaLabel="Search recent activity"
                    regionValue={locationInStore}
                    onRegionChange={setRegionFilter}
                    statusValue={recentStatusFilter}
                    onStatusChange={setRecentStatusFilter}
                    isFetchingMore={isFetchingMore}
                    loadedRegions={loadedRegions}
                    totalRegions={totalRegions}
                    onRefresh={handleRefreshProjects}
                    isRefreshDisabled={projectsLoading || isFetchingMore}
                    refreshTooltip="Refresh recents"
                    refreshAriaLabel="Refresh recents"
                    onCreateProject={handleCreateProject}
                    rightExtras={
                      <Button
                        appearance="subtle"
                        size="small"
                        onClick={clearAllRecent}
                        aria-label="Clear all recent activity"
                      >
                        Clear all
                      </Button>
                    }
                  />
                  {displayedRecent.length === 0 ? (
                    <div className={styles.emptyStateOffset}>
                      <EmptyState
                        title="No matches"
                        description="Try clearing the search, region, or status filters."
                      />
                    </div>
                  ) : (
                    <DataTablePanel
                      flushFooter
                      footer={
                        <TablePaginationFooter
                          totalItems={displayedRecent.length}
                          currentPage={recentPagination.page}
                          pageSize={recentPagination.pageSize}
                          onPageChange={recentPagination.setPage}
                          onPageSizeChange={recentPagination.setPageSize}
                        />
                      }
                    >
                      <Table aria-label="Recent work">
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell
                          className={styles.recentColName}
                          aria-sort={recentSortColumn === 'name' ? (recentSortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                        >
                          <button type="button" className={styles.sortableHeader} onClick={() => handleRecentSort('name')}>
                            Name {recentSortColumn === 'name' && (recentSortDirection === 'asc' ? <ArrowSortUpRegular fontSize={12} /> : <ArrowSortDownRegular fontSize={12} />)}
                          </button>
                        </TableHeaderCell>
                        <TableHeaderCell
                          className={styles.recentColType}
                          aria-sort={recentSortColumn === 'type' ? (recentSortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                        >
                          <button type="button" className={styles.sortableHeader} onClick={() => handleRecentSort('type')}>
                            Type {recentSortColumn === 'type' && (recentSortDirection === 'asc' ? <ArrowSortUpRegular fontSize={12} /> : <ArrowSortDownRegular fontSize={12} />)}
                          </button>
                        </TableHeaderCell>
                        <TableHeaderCell
                          className={styles.recentColDate}
                          aria-sort={recentSortColumn === 'lastVisitedAt' ? (recentSortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                        >
                          <button type="button" className={styles.sortableHeader} onClick={() => handleRecentSort('lastVisitedAt')}>
                            Last accessed {recentSortColumn === 'lastVisitedAt' && (recentSortDirection === 'asc' ? <ArrowSortUpRegular fontSize={12} /> : <ArrowSortDownRegular fontSize={12} />)}
                          </button>
                        </TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedRecent.map((entry) => {
                        const { Icon: TypeIcon, label: typeLabel } = RECENT_ENTRY_TYPE_META[entry.type];
                        return (
                          <TableRow key={entry.href}>
                            <TableCell className={styles.recentColName}>
                              <EntityNameCell
                                icon={<TypeIcon />}
                                name={entry.displayName}
                                href={entry.href}
                              />
                            </TableCell>
                            <TableCell className={styles.recentColType}>
                              <Text className={styles.recentMetadata}>{typeLabel}</Text>
                            </TableCell>
                            <TableCell className={styles.recentColDate}>
                              <Text className={styles.recentMetadata}>{formatRelativeTime(entry.lastVisitedAt)}</Text>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                    </DataTablePanel>
                  )}
                </>
              )
            )}

            {selectedTab === 'projects' && (
              <>
                <DashboardToolbar
                  searchValue={projectSearch}
                  onSearchChange={setProjectSearch}
                  searchPlaceholder="Search projects..."
                  searchAriaLabel="Search projects"
                  regionValue={locationInStore}
                  onRegionChange={setRegionFilter}
                  statusValue={projectStatusFilter}
                  onStatusChange={setProjectStatusFilter}
                  isFetchingMore={isFetchingMore}
                  loadedRegions={loadedRegions}
                  totalRegions={totalRegions}
                  onRefresh={handleRefreshProjects}
                  isRefreshDisabled={projectsLoading || isFetchingMore}
                  refreshTooltip="Refresh projects"
                  refreshAriaLabel="Refresh projects"
                  onCreateProject={handleCreateProject}
                />
                {projectsLoading ? (
                  <TableSkeleton rows={5} columns={6} />
                ) : filteredProjects.length === 0 ? (
                  <div className={styles.emptyStateOffset}>
                    <EmptyState
                      title={locationInStore ? 'No projects in this region' : 'No projects found'}
                      description={locationInStore ? 'Try selecting a different region or "All regions".' : 'Create a project to get started.'}
                      action={{ label: 'Create project', icon: <AddRegular />, onClick: handleCreateProject }}
                    />
                  </div>
                ) : (
                  <DataTablePanel
                    flushFooter
                    footer={
                      <TablePaginationFooter
                        totalItems={filteredProjects.length}
                        currentPage={projectsPagination.page}
                        pageSize={projectsPagination.pageSize}
                        onPageChange={projectsPagination.setPage}
                        onPageSizeChange={projectsPagination.setPageSize}
                      />
                    }
                  >
                    <Table aria-label="Projects">
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell aria-sort={projectSortColumn === 'name' ? (projectSortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                            <button type="button" className={styles.sortableHeader} onClick={() => handleProjectSort('name')}>
                              Name {projectSortColumn === 'name' && (projectSortDirection === 'asc' ? <ArrowSortUpRegular fontSize={12} /> : <ArrowSortDownRegular fontSize={12} />)}
                            </button>
                          </TableHeaderCell>
                          <TableHeaderCell aria-sort={projectSortColumn === 'region' ? (projectSortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                            <button type="button" className={styles.sortableHeader} onClick={() => handleProjectSort('region')}>
                              Region {projectSortColumn === 'region' && (projectSortDirection === 'asc' ? <ArrowSortUpRegular fontSize={12} /> : <ArrowSortDownRegular fontSize={12} />)}
                            </button>
                          </TableHeaderCell>
                          <TableHeaderCell aria-sort={projectSortColumn === 'modified' ? (projectSortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                            <button type="button" className={styles.sortableHeader} onClick={() => handleProjectSort('modified')}>
                              Last Modified {projectSortColumn === 'modified' && (projectSortDirection === 'asc' ? <ArrowSortUpRegular fontSize={12} /> : <ArrowSortDownRegular fontSize={12} />)}
                            </button>
                          </TableHeaderCell>
                          <TableHeaderCell>Status</TableHeaderCell>
                          <TableHeaderCell className={styles.actionsCell} aria-label="Row actions" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedProjects.map((project) => {
                          const projectStatus = mapProvisioningState(
                            project.properties?.provisioningState ?? 'Unknown',
                          );
                          const isReady = projectStatus === 'Ready';
                          const href = isReady
                            ? paths.projectApps(locationInStore || project.location || '', project.name, tenantId)
                            : undefined;
                          const azureArmId = projectArmRefs?.get(armRefKey(project.location ?? '', project.name))?.armId;
                          const azurePortalHref = azureArmId ? azurePortalResourceUrl(azureArmId, tenantId) : undefined;
                          return (
                          <TableRow key={project.id}>
                            <TableCell>
                              <EntityNameCell
                                icon={<Folder16Regular />}
                                name={project.name}
                                {...(href ? { href } : {})}
                                isFavorite={favoriteProjects.has(project.id)}
                                onToggleFavorite={(next) => toggleProjectFavorite(project.id, next)}
                              />
                            </TableCell>
                            <TableCell>{regionDisplayName(project.location)}</TableCell>
                            <TableCell>{formatDateTime(project.systemData?.lastModifiedAt, 'full')}</TableCell>
                            <TableCell>
                              <DotStatusBadge status={projectStatusToDot(projectStatus)} />
                            </TableCell>
                            <TableCell className={styles.actionsCell}>
                              <RowActionsMenu ariaLabel={`Actions for ${project.name}`}>
                                <MenuItem
                                  icon={<Open16Regular />}
                                  disabled={!azurePortalHref}
                                  onClick={() => {
                                    if (azurePortalHref) {
                                      window.open(azurePortalHref, '_blank', 'noopener,noreferrer');
                                    }
                                  }}
                                >
                                  Open in Azure
                                </MenuItem>
                              </RowActionsMenu>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </DataTablePanel>
                )}
              </>
            )}
          </div>
        </SectionErrorBoundary>
      )}
      {/* Create project dialog */}
      <CreateProjectDialog
        open={showCreateProjectDialog}
        onClose={() => setShowCreateProjectDialog(false)}
        onProjectCreated={handleProjectCreated}
      />
    </div>
  );
};
